import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, CalendarIcon, CheckIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { Catalog, Entity, Vendor } from '../CatalogsManagement';
interface CatalogFormProps {
  catalog: Catalog | null;
  vendors: Vendor[];
  entities: Entity[];
  onSave: (catalogData: Partial<Catalog>) => void;
  onCancel: () => void;
  locale: string;
}
export function CatalogForm({
  catalog,
  vendors,
  entities,
  onSave,
  onCancel,
  locale
}: CatalogFormProps) {
  // Form state
  const [formData, setFormData] = useState<Partial<Catalog>>({
    name: '',
    description: '',
    vendorId: '',
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: null,
    isActive: true,
    entities: [],
    sites: []
  });
  // Selected entities and sites for UI
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  // Initialize form with catalog data if editing
  useEffect(() => {
    if (catalog) {
      setFormData({
        name: catalog.name,
        description: catalog.description,
        vendorId: catalog.vendorId,
        activeFrom: catalog.activeFrom,
        activeTo: catalog.activeTo,
        isActive: catalog.isActive,
        entities: catalog.entities,
        sites: catalog.sites
      });
      setSelectedEntityIds(catalog.entities.map(entity => entity.id));
      setSelectedSiteIds(catalog.sites.map(site => site.id));
    }
  }, [catalog]);
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle entity selection
  const handleEntityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedValues: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    setSelectedEntityIds(selectedValues);
    // Find the entity objects
    const selectedEntities = entities.filter(entity => selectedValues.includes(entity.id));
    // Update form data
    setFormData(prev => ({
      ...prev,
      entities: selectedEntities
    }));
    // Clear selected sites that don't belong to selected entities
    const validSites = selectedEntities.flatMap(entity => entity.sites);
    const validSiteIds = validSites.map(site => site.id);
    const newSelectedSiteIds = selectedSiteIds.filter(siteId => validSiteIds.includes(siteId));
    setSelectedSiteIds(newSelectedSiteIds);
    // Update sites in form data
    setFormData(prev => ({
      ...prev,
      sites: validSites.filter(site => newSelectedSiteIds.includes(site.id))
    }));
  };
  // Handle site selection
  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedValues: string[] = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    setSelectedSiteIds(selectedValues);
    // Find all sites from selected entities
    const allSites = selectedEntityIds.length > 0 ? entities.filter(entity => selectedEntityIds.includes(entity.id)).flatMap(entity => entity.sites) : [];
    // Filter to only selected sites
    const selectedSites = allSites.filter(site => selectedValues.includes(site.id));
    // Update form data
    setFormData(prev => ({
      ...prev,
      sites: selectedSites
    }));
  };
  // Get available sites based on selected entities
  const getAvailableSites = () => {
    if (selectedEntityIds.length === 0) return [];
    return entities.filter(entity => selectedEntityIds.includes(entity.id)).flatMap(entity => entity.sites);
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10978">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-10979">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-10980">
          {catalog ? 'Edit Catalog' : 'Create Catalog'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10981">
          <XIcon className="h-5 w-5" data-id="element-10982" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-10983">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-10984">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-10985">
            {/* Catalog Name */}
            <div className="sm:col-span-4" data-id="element-10986">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-10987">
                Catalog Name
              </label>
              <div className="mt-1" data-id="element-10988">
                <input type="text" name="name" id="name" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.name} onChange={handleChange} required data-id="element-10989" />
              </div>
            </div>
            {/* Vendor */}
            <div className="sm:col-span-3" data-id="element-10990">
              <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-10991">
                Vendor
              </label>
              <div className="mt-1" data-id="element-10992">
                <select id="vendorId" name="vendorId" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.vendorId} onChange={handleChange} required data-id="element-10993">
                  <option value="" data-id="element-10994">Select Vendor</option>
                  {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-10995">
                      {vendor.name}
                    </option>)}
                </select>
              </div>
            </div>
            {/* Active Status */}
            <div className="sm:col-span-3 flex items-center" data-id="element-10996">
              <div className="flex h-5 items-center" data-id="element-10997">
                <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={formData.isActive} onChange={handleChange} data-id="element-10998" />
              </div>
              <div className="ml-3 text-sm" data-id="element-10999">
                <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-11000">
                  Active
                </label>
                <p className="text-gray-500" data-id="element-11001">
                  Make this catalog available for use
                </p>
              </div>
            </div>
            {/* Description */}
            <div className="sm:col-span-6" data-id="element-11002">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-11003">
                Description
              </label>
              <div className="mt-1" data-id="element-11004">
                <textarea id="description" name="description" rows={3} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={formData.description} onChange={handleChange} data-id="element-11005" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-11006">
                Brief description of the catalog and its contents
              </p>
            </div>
            {/* Active From */}
            <div className="sm:col-span-3" data-id="element-11007">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-11008">
                Active From
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-11009">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11010">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-11011" />
                </div>
                <input type="date" name="activeFrom" id="activeFrom" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeFrom} onChange={handleChange} required data-id="element-11012" />
              </div>
            </div>
            {/* Active To */}
            <div className="sm:col-span-3" data-id="element-11013">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-11014">
                Active To
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-11015">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11016">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-11017" />
                </div>
                <input type="date" name="activeTo" id="activeTo" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" value={formData.activeTo || ''} onChange={handleChange} data-id="element-11018" />
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-11019">
                Leave blank for no end date
              </p>
            </div>
            {/* Entities */}
            <div className="sm:col-span-3" data-id="element-11020">
              <label htmlFor="entities" className="block text-sm font-medium text-gray-700 flex items-center" data-id="element-11021">
                <BuildingIcon className="h-4 w-4 mr-1" data-id="element-11022" />
                Entities
              </label>
              <div className="mt-1" data-id="element-11023">
                <select id="entities" name="entities" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" multiple size={4} value={selectedEntityIds} onChange={handleEntityChange} data-id="element-11024">
                  {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-11025">
                      {entity.name}
                    </option>)}
                </select>
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-11026">
                Hold Ctrl/Cmd to select multiple
              </p>
            </div>
            {/* Sites */}
            <div className="sm:col-span-3" data-id="element-11027">
              <label htmlFor="sites" className="block text-sm font-medium text-gray-700 flex items-center" data-id="element-11028">
                <HomeIcon className="h-4 w-4 mr-1" data-id="element-11029" />
                Sites
              </label>
              <div className="mt-1" data-id="element-11030">
                <select id="sites" name="sites" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" multiple size={4} value={selectedSiteIds} onChange={handleSiteChange} disabled={selectedEntityIds.length === 0} data-id="element-11031">
                  {getAvailableSites().map(site => <option key={site.id} value={site.id} data-id="element-11032">
                      {site.name} (
                      {entities.find(e => e.id === site.entityId)?.name})
                    </option>)}
                </select>
              </div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-11033">
                {selectedEntityIds.length === 0 ? 'Select at least one entity first' : 'Hold Ctrl/Cmd to select multiple'}
              </p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-11034">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-11035">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11036">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-11037" />
            {catalog ? 'Update Catalog' : 'Create Catalog'}
          </button>
        </div>
      </form>
    </div>;
}